import React, { useContext } from 'react';

import { Building, PieChart, Plus, User, Users } from 'lucide-react';

import { reloadCompany } from 'common/actions/company';
import { RevenueTimeframes } from 'common/company/RevenueHelpers';
import {
  AllCalculationFactors,
  CalculationCompanySpendFactors,
  type DefaultFactors,
} from 'common/constants/ideaFormulaFields';
import { AllUsersSegmentID } from 'common/constants/ideas';
import { CompanyContext } from 'common/containers/CompanyContainer';
import { OpenModalContext } from 'common/containers/ModalContainer';
import { ViewerContext } from 'common/containers/ViewerContainer';
import connect from 'common/core/connect';
import EditSegmentModal from 'common/modals/SavedSegmentsModal/EditSegmentModal';
import {
  CommonFactorOptions,
  getCalculationFunctionOptions,
} from 'common/subdomain/admin/v2/constants';
import Field from 'common/uiV3/Field';
import { Select, type SelectBaseOption } from 'common/uiV3/Select';
import VStack from 'common/uiV3/VStack';
import hasPermission from 'common/util/hasPermission';

import type { Company, CustomField, Segment } from 'common/api/endpoints/companies';
import type {
  FormulaChangeFields,
  FormulaFieldFormState,
} from 'common/subdomain/admin/v2/hooks/useIdeaFieldForm';
import type { Option } from 'common/ui/common/select/SelectCommon';

type OwnProps = {
  formulaFieldState: FormulaFieldFormState;
  setValue: (field: FormulaChangeFields, value: string) => void;
  readOnly: boolean;
};

type ConnectProps = {
  reloadCompany: () => void;
};

type Props = OwnProps & ConnectProps;

// Utils used to calculate the options for formula field dropdowns
const isNumericCustomField = (customField: CustomField) => customField.fieldType === 'number';

const calculateSegmentOptions = (segments: Segment[]): SelectBaseOption[] => {
  return segments
    .map((segment) => ({
      label: (
        <Select.OptionLabel>
          <PieChart />
          {segment.name}
        </Select.OptionLabel>
      ),
      value: segment._id,
    }))
    .concat({
      label: (
        <Select.OptionLabel>
          <Users />
          Everyone
        </Select.OptionLabel>
      ),
      value: AllUsersSegmentID,
    });
};

const calculateFactorAndFieldOptions = (company: Company): Option[] => {
  // TODO: add opportunity factors once we support those with Insights/Ideas
  const companyCustomFieldOptions: SelectBaseOption[] = company.customFields.company
    .filter(isNumericCustomField)
    .map((customField) => ({
      label: (
        <Select.OptionLabel>
          <User />
          {customField.name}
        </Select.OptionLabel>
      ),
      value: customField._id,
    }));

  const userCustomFieldOptions: SelectBaseOption[] = company.customFields.user
    .filter(isNumericCustomField)
    .map((customField) => ({
      label: (
        <Select.OptionLabel>
          <User />
          {customField.name}
        </Select.OptionLabel>
      ),
      value: customField._id,
    }));
  const spendFactorOptions: (SelectBaseOption | null)[] = [
    company.hasCollectedMonthlySpend
      ? {
          label: (
            <Select.OptionLabel>
              <Building />
              Monthly Spend
            </Select.OptionLabel>
          ),
          value: CalculationCompanySpendFactors.monthlySpend,
        }
      : null,
    company.hasCollectedMonthlySpend && company.revenueTimeframe === RevenueTimeframes.arr
      ? {
          label: (
            <Select.OptionLabel>
              <Building />
              Annual Spend
            </Select.OptionLabel>
          ),
          value: CalculationCompanySpendFactors.annualSpend,
        }
      : null,
  ];

  return [
    ...CommonFactorOptions,
    ...companyCustomFieldOptions,
    ...userCustomFieldOptions,
    ...spendFactorOptions,
  ].filter(Boolean) as Option[];
};

function FormulaFieldInputs({
  formulaFieldState,
  setValue,
  readOnly = false,
  reloadCompany,
}: Props) {
  const company = useContext(CompanyContext);
  const openModal = useContext(OpenModalContext);
  const viewer = useContext(ViewerContext);

  const factorAndFieldOptions = calculateFactorAndFieldOptions(company);
  const selectedFactorOrField = factorAndFieldOptions.find((option) =>
    [formulaFieldState.factor, formulaFieldState.fieldID].includes(option.value),
  );

  const segmentOptions = calculateSegmentOptions(company.segments);
  const selectedSegment = segmentOptions.find(
    (option) => option.value === formulaFieldState.segmentID,
  );

  const calculationFunctions = getCalculationFunctionOptions(formulaFieldState.factor);
  const selectedFunction = calculationFunctions.find(
    (option) => option.value === formulaFieldState.calculationFunction,
  );

  const handleCreateSegment = () => {
    // TODO: avoid using `openModal` and use a portal modal instead
    openModal(EditSegmentModal, {
      boards: [],
      onClose: () => {},
      onUpdateQuery: () => {},
      reloadSegments: () => {},
      onSave: async (segment: Segment) => {
        await reloadCompany();
        setValue('segmentID', segment._id);
      },
    });
  };

  // Handle dropdown selection - react-hook-form inspired approach
  const handleFactorOrFieldChange = (value?: string) => {
    if (!value || readOnly) {
      return;
    }

    // Determine if the selected value is a factor or fieldID
    if (AllCalculationFactors.includes(value as DefaultFactors)) {
      setValue('factor', value);
      // Set calculation function if only one option is available
      const functions = getCalculationFunctionOptions(value);
      if (functions.length === 1) {
        setValue('calculationFunction', functions[0].value);
      }
    } else {
      setValue('fieldID', value);
    }
  };

  return (
    <Field>
      <Field.Label>Options</Field.Label>
      <Field.Content>
        <VStack className="gap-2">
          <Select
            allowClear={false}
            disabled={readOnly}
            onValueChange={handleFactorOrFieldChange}
            options={factorAndFieldOptions}
            placeholder="Select factor or field"
            aria-required={true}
            value={selectedFactorOrField?.value}
          />
          <Select
            allowClear={false}
            cta={
              hasPermission('manageUserSegmentation', company, viewer)
                ? {
                    option: {
                      label: (
                        <Select.OptionLabel>
                          <Plus /> Create new segment
                        </Select.OptionLabel>
                      ),
                      value: 'newSegment',
                    },
                    onClick: handleCreateSegment,
                  }
                : undefined
            }
            disabled={readOnly}
            onValueChange={(value) => value && setValue('segmentID', value)}
            options={segmentOptions}
            placeholder="Select segment"
            aria-required={true}
            value={selectedSegment?.value}
          />
          <Select
            allowClear={false}
            disabled={readOnly || calculationFunctions.length === 1}
            onValueChange={(value) => value && setValue('calculationFunction', value)}
            options={calculationFunctions}
            placeholder="Select calculation"
            aria-required={true}
            value={selectedFunction?.value}
          />
        </VStack>
      </Field.Content>
    </Field>
  );
}

// TODO: remove this once we extract the segments from the company object
export default connect(null, { reloadCompany })(
  FormulaFieldInputs,
) as unknown as React.FC<OwnProps>;
